import { Icon } from "@iconify/react";
import { Colors } from "../../../utils/colors";
import { useCallback, useMemo, useState } from "react";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../Containers/PopupModalContainer";
import RectangleButton from "../../Buttons/RectangleButton";
import CustomTextField from "../../CustomTextField";
import {
  NotificationType,
  ProfileContactPageId,
  ProfileHomePageId,
  ProfilePage,
} from "@markit/common.types";
import { generate } from "shortid";
import DragDropList from "../../Lists/DragDropList";
import isEqual from "lodash/isEqual";
import { API } from "../../../API";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { getUserRef } from "../../../utils/FirebaseUtils";
import { updateDoc } from "../../../firebase";
import AlertContainer from "../../Containers/AlertContainer";
import { useOnMount } from "../../../hooks/useOnMount";
import DragDropListIcon from "../../Lists/DragDropListIcon";

type ProfileManagePagesModalProps = {
  setIsVisible: (isVisible: boolean) => void;
  profilePages: ProfilePage[];
  setProfilePages: (profilePages: ProfilePage[]) => void;
  isAddPage: boolean;
};

const ProfileManagePagesModal = (props: ProfileManagePagesModalProps) => {
  const { setIsVisible, profilePages, setProfilePages, isAddPage } = props;
  const dispatch = useDispatch();
  const { uid } = useSelector(getAccountState).account.accountData;
  const [localProfilePages, setLocalProfilePages] =
    useState<ProfilePage[]>(profilePages);
  const [alertText, setAlertText] = useState("");
  const [showError, setShowError] = useState(false);
  const [saving, setSaving] = useState(false);

  const madeChanges = useMemo(
    () => !isEqual(localProfilePages, profilePages),
    [localProfilePages, profilePages]
  );

  const addPageOnPress = useCallback(async () => {
    const newPage: ProfilePage = {
      id: generate(),
      title: "New Page",
      createdAt: new Date().toISOString(),
      sections: [],
    };
    setLocalProfilePages([...localProfilePages, newPage]);
  }, [localProfilePages]);

  const removePageOnPress = useCallback(
    (pageId: string) => {
      setLocalProfilePages(localProfilePages.filter((p) => p.id !== pageId));
    },
    [localProfilePages]
  );

  const handleOnDrag = useCallback(
    (newPages: ProfilePage[]) => {
      setLocalProfilePages(newPages);
    },
    [setLocalProfilePages]
  );

  const saveOnPress = useCallback(async () => {
    // Check if any pages are empty titles
    const emptyTitlePages = localProfilePages.filter((p) => p.title === "");
    if (emptyTitlePages.length > 0) {
      setShowError(true);
      setAlertText(
        "Some pages have empty titles. Please add a title to each page."
      );
      return;
    }

    setSaving(true);
    const existingPagesById = new Map(profilePages.map((p) => [p.id, p]));
    for (let i = 0; i < localProfilePages.length; i++) {
      const localPage = localProfilePages[i];
      const foundPage = existingPagesById.get(localProfilePages[i].id);
      if (!foundPage) {
        // New page added
        await API.profile.createProfilePage({ uid, profilePage: localPage });
      } else if (foundPage.title !== localPage.title) {
        // Updating existing page
        await API.profile.updateProfilePage({ uid, profilePage: localPage });
      }
    }
    const localPagesById = new Set(localProfilePages.map((p) => p.id));
    for (let i = 0; i < profilePages.length; i++) {
      const page = profilePages[i];
      if (!localPagesById.has(page.id)) {
        // Removing existing page
        await API.profile.deleteProfilePage({ uid, profilePageId: page.id });
      }
    }

    // Reorder the pages based on the new order
    const userRef = getUserRef(uid);
    await updateDoc(userRef, {
      profilePagesOrder: localProfilePages.map((page) => page.id),
    });

    setProfilePages(localProfilePages);
    showNotificationBanner(
      dispatch,
      "Pages Saved!",
      NotificationType.AFFIRMATIVE
    );
    setSaving(false);
    setIsVisible(false);
  }, [
    setProfilePages,
    localProfilePages,
    uid,
    dispatch,
    setIsVisible,
    profilePages,
  ]);

  useOnMount(() => {
    if (isAddPage) {
      addPageOnPress();
    }
  });

  return (
    <>
      <PopupModalContainer
        headerComp={<span className="sectionTitle">Manage Pages</span>}
        valueComp={
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <DragDropList items={localProfilePages} handleChange={handleOnDrag}>
              {(page: ProfilePage, _, dragHandleProps: any) => (
                <div className="AlignedRow" style={{ gap: 7 }}>
                  <CustomTextField
                    value={page.title}
                    placeholder="Page Name..."
                    inputMode="text"
                    borderRadius={12}
                    noAutocomplete
                    autoFocus={
                      isAddPage &&
                      page.id ===
                        localProfilePages[localProfilePages.length - 1].id
                    } // Focus on new page on mount if adding new page
                    disabled={page.id === ProfileContactPageId}
                    backgroundColor={Colors.WHITE}
                    onChange={(change: any) => {
                      setShowError(false);
                      setLocalProfilePages(
                        localProfilePages.map((p) =>
                          p.id === page.id
                            ? { ...p, title: change.target.value }
                            : p
                        )
                      );
                    }}
                    maxLength={40}
                    altMarginBottom={0}
                    altPadding="14px 14px"
                    error={showError && !page.title}
                  />
                  {page.id !== ProfileHomePageId &&
                  page.id !== ProfileContactPageId ? (
                    <div>
                      <Icon
                        icon="ion:trash"
                        height={20}
                        style={{ cursor: "pointer", color: Colors.RED3 }}
                        onClick={() => removePageOnPress(page.id)}
                      />
                    </div>
                  ) : null}
                  <DragDropListIcon dragHandleProps={dragHandleProps} />
                </div>
              )}
            </DragDropList>
            <RectangleButton
              buttonLabel="Add Page"
              iconRight={
                <Icon
                  icon="ion:add-circle"
                  height={18}
                  style={{ color: Colors.BLACK }}
                />
              }
              onPress={addPageOnPress}
              altColor={Colors.WHITE}
              altTextColor={Colors.BLACK}
              containerStyles={{
                padding: "14px 12px",
                border: `0.5px solid ${Colors.GRAY2}`,
              }}
            />
          </div>
        }
        fixedBottomContent={
          <div
            className="AlignedRow"
            style={{ gap: 10, paddingInline: 14, paddingTop: 14 }}
          >
            <RectangleButton
              buttonLabel="Cancel"
              onPress={() => setIsVisible(false)}
              altColor={Colors.WHITE1}
              altTextColor={Colors.BLACK}
              containerStyles={{
                padding: "14px 12px",
                border: `0.5px solid ${Colors.GRAY2}`,
              }}
            />
            <RectangleButton
              buttonLabel="Save"
              onPress={saveOnPress}
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
              containerStyles={{
                padding: "14px 12px",
              }}
              disabled={!madeChanges}
              loading={saving}
            />
          </div>
        }
        noExit
        closeModal={() => {
          setIsVisible(false);
        }}
        modalContentWidth={PopupModalContainerWidth.SMALL}
      />
      <AlertContainer
        headerComp={alertText}
        closeModal={() => setAlertText("")}
        hideModal={alertText === ""}
      />
    </>
  );
};

export default ProfileManagePagesModal;
